import { ClickAwayListener, MenuList } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon'
import ListItemText from '@mui/material/ListItemText/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import React from 'react'
import {
  AccountDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc
} from 'types/api/changes'

import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'

import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'

import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined'

import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined'

import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined'

import { t } from 'i18next'
import {
  isGroupArchived,
  isGroupHidden,
  isGroupMuted,
  isGroupUnread
} from 'utils/api/userSettings'
import { Model } from 'types/model'
import { useCalendar } from 'components/providers/CalendarProvider'
import { isGroupcalApp } from 'utils/appType'
import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import { GroupContextMenuProps } from 'components/sidebar/Groups'
import { isConvertedGroup } from 'utils/groups'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import { isGroupBlockedToAddEvents } from 'utils/api/groups'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined'

interface ContextMenuProps {
  group: GroupDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  account?: Model<AccountDoc>
  x: number // X-coordinate of the click event
  y: number // Y-coordinate of the click event
  contextMenuPosition: GroupContextMenuProps
  onClose: () => void // Callback to close the context menu
}

const GroupItemContextMenu: React.FC<ContextMenuProps> = ({
  group,
  userSettings,
  account,
  x,
  y,
  contextMenuPosition,
  onClose
}) => {
  const archiveText = isGroupArchived(userSettings, group._id ?? '')
    ? t('unarchive')
    : t('archive')
  const archiveIcon = isGroupArchived(userSettings, group._id ?? '') ? (
    <UnarchiveOutlinedIcon />
  ) : (
    <ArchiveOutlinedIcon />
  )
  const unreadText = isGroupUnread(userSettings, group._id ?? '')
    ? t('markAsRead')
    : t('markAsUnread')
  const unreadIcon = isGroupUnread(userSettings, group._id ?? '') ? (
    <MarkunreadOutlinedIcon />
  ) : (
    <MarkEmailUnreadOutlinedIcon />
  )
  const allCalendarsText = isGroupHidden(userSettings, group._id ?? '')
    ? t('showOnAllCal', { cal: t('allCalendarsButton') })
    : t('dontShowOnAllCal', { cal: t('allCalendarsButton') })
  const allCalendarsIcon = isGroupHidden(userSettings, group._id ?? '') ? (
    <VisibilityOutlinedIcon />
  ) : (
    <VisibilityOffOutlinedIcon />
  )

  const remindersText = isGroupMuted(userSettings, group._id ?? '')
    ? t('enableNotifications')
    : t('disableNotifications')
  const remindersIcon = isGroupMuted(userSettings, group._id ?? '') ? (
    <NotificationsActiveOutlinedIcon />
  ) : (
    <NotificationsOffOutlinedIcon />
  )

  const calendar = useCalendar()

  const showCalendarDetails = () => {
    if (group._id) {
      calendar.onGroupServer(group._id)
      setTimeout(() => {
        calendar.onGroupInfo(group._id)
      }, 50)
    }

    onClose()
  }

  const handleArchiveCalendar = () => {
    // Logic for "Archive calendar" action
    onClose()
    const newValue = isGroupArchived(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? GroupStatus.ACTIVE
      : GroupStatus.ARCHIVED
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        GroupStatus: newValue
      }
    ).then(() => {
      calendar.onGroupServer('')
    })
  }

  const handleMarkAsUnread = () => {
    // Logic for "Mark as unread" action
    onClose()
    const newValue = isGroupUnread(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? '0'
      : '1'
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        Unread: newValue
      }
    )
  }

  const handleHideFromCalendars = () => {
    // Logic for "Don't show on all calendars" action
    onClose()

    const newValue = isGroupHidden(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? '1'
      : '0'
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ShowOnAllCalendars: newValue
      }
    )
  }

  const makeDefaultCalendar = () => {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        Default: '1'
      }
    ).then(() => {
      onClose()
    })
  }

  function handleReminderState(): void {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ActivateReminders: isGroupMuted(userSettings, group._id ?? '')
          ? '1'
          : '0'
      }
    ).then(() => {
      onClose()
    })
  }

  return (
    <ClickAwayListener onClickAway={onClose}>
      <ul
        style={{
          position: 'absolute',
          width: 'fit-content',
          top: y,
          left: x,
          listStyleType: 'none',
          zIndex: 99999,
          padding: '8px',
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
        }}
      >
        <MenuList>
          {isGroupcalApp() && (
            <MenuItem id="archive-group-button" onClick={handleArchiveCalendar}>
              <ListItemIcon>{archiveIcon}</ListItemIcon>
              <ListItemText>{archiveText}</ListItemText>
            </MenuItem>
          )}
          {isGroupcalApp() && (
            <MenuItem onClick={handleMarkAsUnread}>
              <ListItemIcon>{unreadIcon}</ListItemIcon>
              <ListItemText>{unreadText}</ListItemText>
            </MenuItem>
          )}
          {
            <MenuItem
              id="show-on-all-calendar-button"
              onClick={handleHideFromCalendars}
            >
              <ListItemIcon>{allCalendarsIcon}</ListItemIcon>
              <ListItemText>{allCalendarsText}</ListItemText>
            </MenuItem>
          }
          {
            <MenuItem onClick={handleReminderState}>
              <ListItemIcon>{remindersIcon}</ListItemIcon>
              <ListItemText>{remindersText}</ListItemText>
            </MenuItem>
          }
          <MenuItem
            id="show-group-details-button"
            onClick={showCalendarDetails}
          >
            <ListItemIcon>
              <EventNoteOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{t('calendarDetails')}</ListItemText>
          </MenuItem>
          {account &&
          !isGroupBlockedToAddEvents(
            account,
            group,
            userSettings,
            LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
          ) ? (
            <MenuItem onClick={makeDefaultCalendar}>
              <ListItemIcon>
                <StarOutlineRoundedIcon />
              </ListItemIcon>
              <ListItemText>{t('defaultCalendar')}</ListItemText>
            </MenuItem>
          ) : (
            <></>
          )}
        </MenuList>
      </ul>
    </ClickAwayListener>
  )
}

export default GroupItemContextMenu
